import { ImageIcon, Loader2, Maximize2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { trpc } from '../../lib/trpc';
import { cn } from '../../lib/utils';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  DialogTrigger,
} from '../../ui/dialog';
import { VisuallyHidden } from '../visually-hidden';

interface LazyAttachmentImageProps {
  fileId: number;
  fileName: string;
  className?: string;
  onClick?: () => void;
}

export function LazyAttachmentImage({ fileId, fileName, className }: LazyAttachmentImageProps) {
  const [isInView, setIsInView] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      { rootMargin: '200px' } // Load a bit before it enters view
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const {
    data: url,
    isLoading,
    error,
  } = trpc.files.getViewUrl.useQuery({ id: fileId }, { enabled: isInView });

  return (
    <div
      ref={containerRef}
      className={cn(
        'relative aspect-video bg-muted rounded-lg overflow-hidden flex items-center justify-center group/img',
        className
      )}
    >
      {!isInView || isLoading ? (
        <div className="flex flex-col items-center gap-2 text-muted-foreground/40">
          <Loader2 className="size-5 animate-spin" />
        </div>
      ) : error ? (
        <div className="flex flex-col items-center gap-2 text-destructive/50">
          <ImageIcon className="size-5" />
          <span className="text-[10px]">Failed to load</span>
        </div>
      ) : (
        <>
          <img src={url} alt={fileName} className="w-full h-full object-cover" loading="lazy" />

          <Dialog>
            <DialogTrigger asChild>
              <button className="absolute inset-0 bg-black/0 group-hover/img:bg-black/20 transition-colors flex items-center justify-center opacity-0 group-hover/img:opacity-100">
                <div className="bg-white/20 backdrop-blur-md rounded-full p-2 text-white">
                  <Maximize2 className="size-4" />
                </div>
              </button>
            </DialogTrigger>
            <DialogContent className="max-w-[95vw] max-h-[95vh] p-0 overflow-hidden bg-transparent border-none shadow-none flex items-center justify-center">
              <VisuallyHidden>
                <DialogTitle>{fileName}</DialogTitle>
                <DialogDescription>Full screen view of {fileName}</DialogDescription>
              </VisuallyHidden>
              <img
                src={url}
                alt={fileName}
                className="max-w-full max-h-full object-contain rounded-lg"
              />
            </DialogContent>
          </Dialog>
        </>
      )}
    </div>
  );
}
